/* eslint-disable @typescript-eslint/no-unsafe-function-type */
import { readGaClientId, readGaSessionId } from './attribution/googleAnalytics';

type WindowWithDataLayer = Window & {
  gtag: Function;
  grecaptcha: {
    execute: Function;
  };
};

declare const window: WindowWithDataLayer;

const grecaptcha = window.grecaptcha || {};

const TARGET_ID = import.meta.env
  .VITE_REACT_APP_GOOGLE_ANALYTICS_MEASUREMENT_ID!;

// GA4 is loaded through GTM, so `gtag('get', ...)` only calls back once the
// container and its GA4 tag have actually run. Cap the wait so a blocked or
// slow container can never hold up a payment.
const GTAG_GET_TIMEOUT_MS = 500;

type GoogleAnalyticsField = 'client_id' | 'session_id';

type GoogleAnalyticsIds = {
  gClientId: string;
  gSessionId: string;
};

function gtag(...params: any[]) {
  window.gtag?.(...params);
}

function readFromGtag(field: GoogleAnalyticsField): Promise<string> {
  return new Promise((resolve) => {
    let settled = false;

    const settle = (value: string) => {
      if (settled) return;
      settled = true;
      resolve(value || '');
    };

    const timer = setTimeout(() => settle(''), GTAG_GET_TIMEOUT_MS);

    if (!TARGET_ID || typeof window.gtag !== 'function') {
      clearTimeout(timer);
      settle('');
      return;
    }

    try {
      window.gtag('get', TARGET_ID, field, (value: string) => {
        clearTimeout(timer);
        settle(value);
      });
    } catch {
      clearTimeout(timer);
      settle('');
    }
  });
}

const EMPTY_IDS: GoogleAnalyticsIds = { gClientId: '', gSessionId: '' };

/**
 * Live gtag first, GA's own cookies as the fallback. The cookies survive the
 * cases that used to silently yield an empty id: the GA4 tag not having
 * registered yet when the user clicks pay, or the `get` callback never firing.
 *
 * Never rejects. Analytics is best-effort and these ids are read inside the
 * payment flow, so a failure here must degrade to an empty id rather than
 * propagate to the caller.
 */
async function getGoogleAnalyticsClientId(): Promise<string> {
  try {
    const clientId = await readFromGtag('client_id');

    return clientId || readGaClientId();
  } catch (err) {
    console.log(err);
    return '';
  }
}

async function getGoogleAnalyticsSessionId(): Promise<string> {
  try {
    const sessionId = await readFromGtag('session_id');

    return sessionId || readGaSessionId(TARGET_ID);
  } catch (err) {
    console.log(err);
    return '';
  }
}

/**
 * Both ids for one request. Resolves them in parallel so the pay button waits
 * at most GTAG_GET_TIMEOUT_MS in total rather than once per id.
 *
 * Never rejects, for the same reason as the getters above.
 */
async function getGoogleAnalyticsIds(): Promise<GoogleAnalyticsIds> {
  try {
    const [gClientId, gSessionId] = await Promise.all([
      getGoogleAnalyticsClientId(),
      getGoogleAnalyticsSessionId(),
    ]);

    return { gClientId, gSessionId };
  } catch (err) {
    console.log(err);
    return EMPTY_IDS;
  }
}

function sendEvent(eventName: string, params: Record<string, any> = {}) {
  gtag('event', eventName, params);
}

async function reportSignup() {
  try {
    const gClientId = await getGoogleAnalyticsClientId();
    if (gClientId) {
      sendEvent('web_registration', { client_id: gClientId });
    }
  } catch (err) {
    console.log(err);
  }
}

async function reportAiTrace() {
  try {
    const gClientId = await getGoogleAnalyticsClientId();
    if (gClientId) {
      sendEvent('trace_completed', {
        client_id: gClientId,
        form_name: 'ai_investigation',
      });
    }
  } catch (err) {
    console.log(err);
  }
}

async function executeCaptcha() {
  return new Promise<string>((resolve, reject) => {
    grecaptcha
      .execute(import.meta.env.VITE_REACT_APP_GOOGLE_RECAPTCHA_KEY as string, {
        action: 'submit',
      })
      .then((token: string) => {
        resolve(token);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

export type { GoogleAnalyticsIds };

export {
  gtag,
  getGoogleAnalyticsClientId,
  getGoogleAnalyticsSessionId,
  getGoogleAnalyticsIds,
  sendEvent,
  reportSignup,
  executeCaptcha,
  reportAiTrace,
};
